import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from 'services/api';
import useAuthStore from 'stores/authStore';
import { Button } from 'components/ui/button';
import { Badge } from 'components/ui/badge';
import {
  Bell, Check, CheckCheck, Trash2, X, Calendar,
  MessageCircle, UserPlus, Clock, AlertCircle, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

export default function NotificationCenter() {
  const token = useAuthStore((s) => s.token);
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [respondingTo, setRespondingTo] = useState(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    fetchNotifications();
    // Poll for new notifications every 30 seconds
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [token]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchNotifications = async () => {
    if (!token) return;
    try {
      const response = await api.get('/notification-center');
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await api.put(`/notification-center/${notificationId}/read`, {});
      setNotifications(prev => 
        prev.map(n => n.notification_id === notificationId ? {...n, read: true} : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      toast.error('Failed to mark as read');
    }
  };

  const markAllAsRead = async () => {
    try {
      await api.put('/notification-center/read-all', {});
      setNotifications(prev => prev.map(n => ({...n, read: true})));
      setUnreadCount(0);
      toast.success('All notifications marked as read');
    } catch (error) {
      toast.error('Failed to mark all as read');
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      await api.delete(`/notification-center/${notificationId}`);
      setNotifications(prev => prev.filter(n => n.notification_id !== notificationId));
      toast.success('Notification deleted');
    } catch (error) {
      toast.error('Failed to delete notification');
    }
  };

  const handleRespondToRequest = async (notification, action) => {
    const requestId = notification.data?.request_id;
    if (!requestId) return;
    setRespondingTo(requestId);
    try {
      await api.put(`/caregivers/requests/${requestId}/respond`, { action });
      toast.success(action === 'accept' ? 'Request accepted!' : 'Request declined');
      await markAsRead(notification.notification_id);
      setNotifications(prev =>
        prev.map(n => n.notification_id === notification.notification_id
          ? {...n, read: true, _responded: action} : n
        )
      );
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action}`);
    } finally {
      setRespondingTo(null);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'time_off_request':
      case 'time_off_response':
        return <Calendar className="w-4 h-4 text-blue-500" />;
      case 'caregiver_request':
      case 'caregiver_response':
        return <UserPlus className="w-4 h-4 text-teal-500" />;
      case 'message':
        return <MessageCircle className="w-4 h-4 text-purple-500" />;
      case 'shift_reminder':
        return <Clock className="w-4 h-4 text-orange-500" />;
      default:
        return <Bell className="w-4 h-4 text-slate-500" />;
    }
  };

  const formatTime = (dateString) => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch {
      return 'Just now';
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setIsOpen(!isOpen)}
        className="relative"
        data-testid="notification-bell"
      >
        <Bell className="w-5 h-5 text-slate-600" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </Button>

      {isOpen && (
        <div className="fixed inset-x-0 top-14 mx-2 sm:absolute sm:inset-auto sm:right-0 sm:top-auto sm:mt-2 sm:mx-0 w-auto sm:w-96 bg-white rounded-lg shadow-xl border border-slate-200 z-50">
          {/* Header */}
          <div className="flex items-center justify-between p-3 border-b border-slate-100">
            <h3 className="font-semibold text-slate-900 text-sm">Notifications</h3>
            <div className="flex items-center gap-1">
              {unreadCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={markAllAsRead}
                  className="text-xs text-teal-600 hover:text-teal-700 h-7 px-2"
                >
                  <CheckCheck className="w-3.5 h-3.5" />
                  Mark all read
                </Button>
              )}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsOpen(false)}
                className="h-7 w-7"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Notifications List */}
          <div className="max-h-[60vh] sm:max-h-96 overflow-y-auto overscroll-contain">
            {notifications.length === 0 ? (
              <div className="p-8 text-center">
                <Bell className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No notifications yet</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {notifications.map((notification) => (
                  <div
                    key={notification.notification_id}
                    className={`p-3 hover:bg-slate-50 transition-colors ${!notification.read ? 'bg-teal-50/50' : ''}`}
                    data-testid={`notification-${notification.notification_id}`}
                  >
                    <div className="flex items-start gap-2.5">
                      <div className="mt-0.5 shrink-0">
                        {getNotificationIcon(notification.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-1">
                          <p className="font-medium text-xs sm:text-sm text-slate-900 line-clamp-2 leading-tight">
                            {notification.title}
                            {!notification.read && (
                              <span className="inline-block w-1.5 h-1.5 bg-teal-500 rounded-full ml-1 align-middle" />
                            )}
                          </p>
                          <div className="flex items-center shrink-0">
                            {!notification.read && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => markAsRead(notification.notification_id)}
                                className="h-6 w-6 text-slate-400 hover:text-teal-600"
                              >
                                <Check className="w-3.5 h-3.5" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => deleteNotification(notification.notification_id)}
                              className="h-6 w-6 text-slate-400 hover:text-red-500"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </div>
                        <p className="text-xs text-slate-600 mt-0.5 line-clamp-2">
                          {notification.message}
                        </p>
                        <p className="text-[10px] sm:text-xs text-slate-400 mt-1">
                          {formatTime(notification.created_at)}
                        </p>
                        {notification.type === 'caregiver_request' && notification.data?.request_id && !notification._responded && !notification.data?.responded && (
                          <div className="flex items-center gap-1.5 mt-2">
                            <Button
                              size="sm"
                              onClick={() => handleRespondToRequest(notification, 'accept')}
                              disabled={respondingTo === notification.data.request_id}
                              className="bg-teal-500 hover:bg-teal-600 h-7 text-xs px-2.5"
                            >
                              {respondingTo === notification.data.request_id
                                ? <Loader2 className="w-3 h-3 animate-spin" />
                                : <Check className="w-3 h-3" />}
                              Accept
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleRespondToRequest(notification, 'decline')}
                              disabled={respondingTo === notification.data.request_id}
                              className="border-red-200 text-red-600 hover:bg-red-50 h-7 text-xs px-2.5"
                            >
                              <X className="w-3 h-3" />
                              Decline
                            </Button>
                          </div>
                        )}
                        {(notification._responded || notification.data?.responded) && (
                          <span className={`inline-block mt-1.5 text-[10px] font-medium px-2 py-0.5 rounded-full ${(notification._responded || notification.data?.responded) === 'accept' ? 'bg-teal-100 text-teal-700' : 'bg-red-100 text-red-700'}`}>
                            {(notification._responded || notification.data?.responded) === 'accept' ? '✓ Accepted' : '✗ Declined'}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="p-3 border-t border-slate-100 text-center">
              <Button
                variant="link"
                className="text-sm text-teal-600"
                onClick={() => {
                  setIsOpen(false);
                  navigate('/notifications');
                }}
              >
                View all notifications
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
